// src/services/ImageService.js

/**
 * Service de gestion des images
 * Permet de gérer les URL des images, les fallbacks et les préchargements
 * 
 * VERSION AVEC CACHE BUSTING
 * Incrémentez APP_VERSION à chaque mise à jour d'images produits
 */
class ImageService {
  // ✅ VERSION POUR CACHE BUSTING - À INCRÉMENTER MANUELLEMENT
  static APP_VERSION = '1.0.2'; // ✅ INCRÉMENTÉ pour les nouvelles images
  
  // URL de base pour les images de produits (configurable)
  static BASE_URL = process.env.REACT_APP_IMAGES_BASE_URL || '/images';
  
  // Liste des images génériques pour les fallbacks par catégorie
  static GENERIC_IMAGES = {
    eau: 'water-generic.png',
    soda: 'soda-generic.png',
    beer: 'beer-generic.png',
    default: 'drink-generic.png'
  };
  
  // Image placeholder par défaut
  static PLACEHOLDER = 'placeholder-product.png';
  
  /**
   * Ajoute le cache busting à une URL
   * @param {string} url - URL de base
   * @returns {string} - URL avec paramètre de version
   */
  static addCacheBusting(url) {
    if (!url) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${this.APP_VERSION}`;
  }
  
  /**
   * Génère les différents chemins possibles pour une image de produit
   * @param {string} productCode - Code du produit
   * @param {string} size - Taille de l'image (large, small, etc.)
   * @returns {Array} - Tableau de chemins d'accès à essayer (AVEC cache busting)
   */
  static getProductImagePaths(productCode, size = '') {
    if (!productCode) return [this.addCacheBusting(this.getPlaceholderUrl())];
    
    const suffix = size ? `-${size}` : '';
    const mainCode = productCode.split('-')[0];
    
    const paths = [
      // Chemin complet avec suffixe
      `${this.BASE_URL}/products/${productCode}${suffix}.png`,
      // Sans suffixe
      `${this.BASE_URL}/${productCode}.png`,
      // Juste le code principal
      `${this.BASE_URL}/${mainCode}${suffix}.png`,
      `${this.BASE_URL}/${mainCode}.png`,
      // Chemin relatif au dossier public
      `/images/${productCode}${suffix}.png`,
      `/images/${productCode}.png`,
      `/images/${mainCode}${suffix}.png`,
      `/images/${mainCode}.png`
    ];
    
    // ✅ AJOUT DU CACHE BUSTING À TOUTES LES URLS
    return paths.map(path => this.addCacheBusting(path));
  }
  
  /**
   * Détermine la catégorie d'un produit basé sur son nom ou code
   * @param {string} productNameOrCode - Nom ou code du produit
   * @returns {string} - Catégorie (eau, soda, beer, default)
   */
  static getCategoryFromProduct(productNameOrCode) {
    const normalized = productNameOrCode.toLowerCase();
    
    if (normalized.includes('eau') || 
        normalized.includes('vitale') || 
        normalized.includes('tangui') || 
        normalized.includes('vit-') || 
        normalized.includes('tng-')) {
      return 'eau';
    }
    
    if (normalized.includes('cola') || 
        normalized.includes('soda') || 
        normalized.includes('gazeu') || 
        normalized.includes('top-') || 
        normalized.includes('djino')) {
      return 'soda';
    }
    
    if (normalized.includes('bière') || 
        normalized.includes('beer') || 
        normalized.includes('33-') || 
        normalized.includes('export')) {
      return 'beer';
    }
    
    return 'default';
  }
  
  /**
   * Obtient l'URL de l'image générique pour une catégorie
   * @param {string} category - Catégorie (eau, soda, beer, default)
   * @returns {string} - URL de l'image générique (AVEC cache busting)
   */
  static getGenericImageUrl(category) {
    const imageName = this.GENERIC_IMAGES[category] || this.GENERIC_IMAGES.default;
    return this.addCacheBusting(`${this.BASE_URL}/${imageName}`);
  }
  
  /**
   * Obtient l'URL de l'image placeholder
   * @returns {string} - URL de l'image placeholder (AVEC cache busting)
   */
  static getPlaceholderUrl() {
    return this.addCacheBusting(`${this.BASE_URL}/${this.PLACEHOLDER}`);
  }
  
  /**
   * Obtient la meilleure URL d'image pour un produit avec fallbacks
   * @param {Object} product - Produit
   * @param {string} size - Taille de l'image (large, small, etc.)
   * @returns {Object} - Objet contenant l'URL principale et les fallbacks (TOUS AVEC cache busting)
   */
  static getProductImageUrls(product, size = '') {
    if (!product) return { 
      primary: this.getPlaceholderUrl(), 
      fallbacks: [] 
    };
    
    const code = product.code_produit || product.code || '';
    const paths = this.getProductImagePaths(code, size);
    const category = this.getCategoryFromProduct(product.nom || product.displayName || code);
    
    // Ajouter les fallbacks génériques et le placeholder
    paths.push(this.getGenericImageUrl(category));
    paths.push(this.getPlaceholderUrl());
    
    return {
      primary: paths[0],
      fallbacks: paths.slice(1)
    };
  }
  
  /**
   * ✅ Génère l'URL pour l'image bouteille avec cache busting
   * Utilisée dans ProductCard et les modals
   * @param {string} productCode - Code du produit
   * @returns {string} - URL de l'image bouteille avec cache busting
   */
  static getBottleImageUrl(productCode) {
    if (!productCode) return this.getPlaceholderUrl();
    return this.addCacheBusting(`/images/products/${productCode}-bottle.png`);
  }
  
  /**
   * ✅ NOUVELLE MÉTHODE : Génère l'URL pour l'image pack avec cache busting
   * Utilisée dans la modal de CartPage pour afficher l'image du pack
   * @param {string} productCode - Code du produit
   * @returns {string} - URL de l'image pack avec cache busting
   */
  static getPackImageUrl(productCode) {
    if (!productCode) return this.getPlaceholderUrl();
    return this.addCacheBusting(`/images/products/${productCode}-large.png`);
  }
  
  /**
   * ✅ NOUVELLE MÉTHODE : Détecte si un produit est vendu en unité (bidon)
   * Un produit est considéré comme "unité" si bouteilles_par_pack === 1
   * Exemples : Vitale 10L, Tangui 10L, etc.
   * @param {Object} product - Objet produit
   * @returns {boolean} - True si le produit est vendu en unité
   */
  static isUnitProduct(product) {
    if (!product) return false;
    
    // Vérifier si bouteilles_par_pack existe et est égal à 1
    const bottlesPerPack = product.bouteilles_par_pack || product.bottlesPerPack || 0;
    return bottlesPerPack === 1;
  }
  
  /**
   * Précharge une liste d'images
   * @param {Array} urls - Tableau d'URLs à précharger
   */
  static preloadImages(urls) {
    urls.forEach(url => {
      if (typeof url === 'string') {
        const img = new Image();
        img.src = url;
      }
    });
  }
}

export default ImageService;